<script setup lang="ts">
import { computed, ref } from 'vue';
import { RouterLink, useRoute, useRouter } from 'vue-router';
import { signOut } from '@/features/auth/api';
import NavIcon, { type NavIconName } from '@/app/NavIcon.vue';
import GlobalSearch from '@/features/search/GlobalSearch.vue';
import NotificationBell from '@/features/notifications/components/NotificationBell.vue';
import { usePendingActions } from '@/features/pending/queries';
import { useBrandingStore } from '@/shared/branding/branding.store';
import { useSessionStore } from '@/shared/stores/session';
import UserAvatar from '@/shared/ui/UserAvatar.vue';
import AppButton from '@/shared/ui/AppButton.vue';
import AppModal from '@/shared/ui/AppModal.vue';

/**
 * WEB-004 — mobile first. Below `lg` the navigation is a bottom bar with 44px targets,
 * which is where a thumb reaches; from `lg` up it becomes the graphite rail, which is
 * where a pointer expects it.
 *
 * The rail groups destinations under headings. Seven flat entries is a list somebody
 * scans; three groups of two or three is a structure somebody learns — and the groups say
 * something true about the product: people, communication, and your own things.
 */
const session = useSessionStore();
const branding = useBrandingStore();
const route = useRoute();
const router = useRouter();

const signOutOpen = ref(false);
const signingOut = ref(false);

interface NavItem {
  name: string;
  label: string;
  icon: NavIconName;
  permission: string | null;
}

interface NavGroup {
  label: string | null;
  items: NavItem[];
}

const GROUPS: readonly NavGroup[] = [
  {
    label: null,
    items: [{ name: 'dashboard', label: 'Inicio', icon: 'home', permission: null }],
  },
  {
    label: 'Personas',
    items: [
      { name: 'employees', label: 'Directorio', icon: 'people', permission: null },
      { name: 'org-chart', label: 'Organigrama', icon: 'org', permission: 'orgchart.read' },
    ],
  },
  {
    label: 'Comunicación',
    items: [
      { name: 'news', label: 'Comunicados', icon: 'news', permission: 'news.read' },
      { name: 'documents', label: 'Políticas', icon: 'doc', permission: 'document.read' },
    ],
  },
  {
    label: 'Mi espacio',
    items: [
      { name: 'my-profile', label: 'Mi perfil', icon: 'person', permission: null },
      { name: 'leave', label: 'Ausencias', icon: 'calendar', permission: 'leave.read' },
      {
        name: 'leave-approvals',
        label: 'Por resolver',
        icon: 'check',
        permission: 'leave.approve',
      },
      { name: 'benefits', label: 'Beneficios', icon: 'gift', permission: 'benefit.read' },
      { name: 'training', label: 'Capacitación', icon: 'learn', permission: 'training.read' },
    ],
  },
  {
    label: null,
    items: [{ name: 'help', label: 'Centro de ayuda', icon: 'help', permission: null }],
  },
  {
    label: 'Administración',
    items: [
      {
        name: 'leave-overview',
        label: 'Vacaciones',
        icon: 'calendar',
        permission: 'leave.manage',
      },
      {
        name: 'work-calendars',
        label: 'Calendarios',
        icon: 'calendar',
        permission: 'calendar.manage',
      },
      { name: 'payroll-periods', label: 'Planillas', icon: 'money', permission: 'payroll.read' },
      { name: 'aguinaldo', label: 'Aguinaldo', icon: 'gift', permission: 'payroll.read' },
      { name: 'severance', label: 'Liquidaciones', icon: 'doc', permission: 'severance.read' },
      { name: 'payroll-parameters', label: 'Parámetros', icon: 'brand', permission: 'payroll.read' },
      { name: 'payroll', label: 'Liquidación', icon: 'money', permission: 'compensation.read' },
      { name: 'branding', label: 'Marca', icon: 'brand', permission: 'branding.manage' },
    ],
  },
];

/** A group whose every entry is hidden by permission disappears with its heading. */
const groups = computed(() =>
  GROUPS.map((group) => ({
    ...group,
    items: group.items.filter(
      (item) => item.permission === null || session.can(item.permission),
    ),
  })).filter((group) => group.items.length > 0),
);

/** The bottom bar is flat: groups need vertical space a phone does not have. */
const flatNavigation = computed(() => groups.value.flatMap((group) => group.items));

const isCurrent = (name: string): boolean => route.name === name;

/**
 * How many absences are waiting on this person.
 *
 * Shown beside the destination rather than only on the bell: a notification is read once
 * and gone, and the work it announced stays. The same query as the home panel, so the two
 * can never disagree.
 */
const pending = usePendingActions();

const approvalCount = computed(
  () =>
    (pending.data.value?.groups ?? [])
      .filter((group) => group.kind === 'aprobar-ausencias' || group.kind === 'registrar-formulario')
      .reduce((total, group) => total + group.total, 0),
);

const displayName = computed(() => session.user?.email.split('@')[0] ?? '');
/** `nombre.apellido` becomes "Nombre Apellido" so the avatar can do the rest. */
const avatarName = computed(() => displayName.value.split(/[._-]/).filter(Boolean).join(' '));

async function confirmSignOut(): Promise<void> {
  signingOut.value = true;
  try {
    await signOut();
    session.clear();
    await router.replace({ name: 'login' });
  } finally {
    signingOut.value = false;
    signOutOpen.value = false;
  }
}
</script>

<template>
  <div class="min-h-dvh lg:flex">
    <!-- PQ-007 — the first stop for a keyboard user, so they can skip the navigation. -->
    <a
      href="#contenido"
      class="skip-link"
    >Ir al contenido principal</a>

    <header
      class="sticky top-0 z-20 flex items-center justify-between border-b border-border bg-surface px-4 py-3 lg:hidden"
    >
      <!--
        `min-w-0` plus `truncate` because the name is up to 60 characters and the actions
        beside it are not optional. Without it a long name pushes "Salir" off a 390px
        screen. The logo keeps its own width: shrinking a mark is worse than clipping text.
      -->
      <p class="flex min-w-0 items-center gap-2 font-display text-base font-semibold text-ink">
        <img
          v-if="branding.branding.logoUrl"
          :src="branding.branding.logoUrl"
          alt=""
          class="max-h-6 w-auto max-w-24 shrink-0 object-contain"
        >
        <span class="truncate">{{ branding.branding.siteName }}</span>
      </p>
      <div class="flex min-w-0 items-center gap-1">
        <!-- The same field, in the header's own colours. -->
        <GlobalSearch class="w-36 sm:w-56" />
        <NotificationBell />
        <AppButton
          variant="ghost"
          @click="signOutOpen = true"
        >
          Salir
        </AppButton>
      </div>
    </header>

    <nav
      class="hidden w-60 shrink-0 flex-col self-start overflow-hidden bg-rail py-7 lg:sticky lg:top-0 lg:flex lg:h-dvh"
      aria-label="Navegación principal"
    >
      <!--
        With a logo, the logo IS the mark: the word mark stands down, because two marks
        side by side is one mark too many — and because a long name beside an image wraps
        to two lines in a 15rem rail.

        The mark sits directly on the rail, with no plate behind it. That trades a
        guarantee for a cleaner rail: a logo drawn for a white background can disappear
        against graphite. The branding screen previews it on both backgrounds precisely so
        that is discovered before saving rather than after.
      -->
      <div class="shrink-0 px-6 pb-7">
        <!--
          Centred and given the full width between the rail's gutters. `max-w-full` rather
          than a fixed width so a wide mark and a square one are both as large as they can
          be without touching the edges; `max-h` is what stops a tall one from pushing the
          navigation down.
        -->
        <img
          v-if="branding.branding.logoUrl"
          :src="branding.branding.logoUrl"
          :alt="branding.branding.siteName"
          class="mx-auto block max-h-20 w-auto max-w-full object-contain"
        >

        <div
          v-else
          class="flex items-baseline gap-2"
        >
          <span class="font-display text-xl font-semibold tracking-wide text-[#f2f0ea]">
            {{ branding.branding.siteName }}
          </span>
          <!-- The brass dot is the whole brand mark: one accent, stated once. -->
          <span
            class="inline-block size-[7px] rounded-full bg-brand-500"
            aria-hidden="true"
          />
        </div>
      </div>

      <!--
        Only the list scrolls, not the whole rail.

        With the rail scrolling as one, a taller logo or a role with more entries pushed
        the footer — and with it "Cerrar sesión" — below the fold, where it could only be
        reached by scrolling a sidebar nobody expects to scroll. Pinning the footer makes
        the brand block's height stop being a usability question.
      -->
      <div class="shrink-0 px-3.5 pb-4">
        <GlobalSearch on-rail />
      </div>

      <div class="min-h-0 flex-1 overflow-y-auto">
        <template
          v-for="(group, index) in groups"
          :key="group.label ?? index"
        >
          <p
            v-if="group.label"
            class="mt-4 mb-2 px-6 text-[10.5px] font-medium tracking-[0.12em] text-[#63676f] uppercase"
          >
            {{ group.label }}
          </p>

          <ul class="flex flex-col gap-0.5 px-3.5">
            <li
              v-for="item in group.items"
              :key="item.name"
            >
              <RouterLink
                :to="{ name: item.name }"
                :aria-current="isCurrent(item.name) ? 'page' : undefined"
                :class="[
                  isCurrent(item.name)
                    ? 'bg-brand-500/15 text-[#f2eddd] [&_svg]:text-brand-500'
                    : 'text-[#9ea2ab] hover:bg-white/5 hover:text-[#e8e6df]',
                  'flex min-h-11 items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                ]"
              >
                <NavIcon :name="item.icon" />
                <span class="flex-1">{{ item.label }}</span>
                <!--
                  El número al lado del destino, no en una campana: una notificación se lee
                  una vez y desaparece; esto sigue ahí mientras el trabajo siga ahí.
                -->
                <span
                  v-if="item.name === 'leave-approvals' && approvalCount > 0"
                  class="rounded-full bg-warning-600/20 px-2 py-0.5 text-xs font-semibold text-warning-600"
                >{{ approvalCount }}</span>
              </RouterLink>
            </li>
          </ul>
        </template>
      </div>

      <div
        class="mt-4 flex shrink-0 items-center gap-2.5 border-t border-white/10 px-5 pt-4 text-xs"
      >
        <UserAvatar
          :name="avatarName"
          brand
          class="size-[30px] text-[10px]"
        />
        <span class="min-w-0 flex-1">
          <span class="block truncate text-[12.5px] font-medium text-rail-ink">
            {{ displayName }}
          </span>
          <span class="block truncate text-[11px] text-rail-ink-muted">
            {{ session.user?.email }}
          </span>
        </span>

        <NotificationBell />

        <button
          type="button"
          class="shrink-0 rounded-lg p-1.5 text-rail-ink-muted hover:bg-white/6 hover:text-[#e8e6df]"
          aria-label="Cerrar sesión"
          @click="signOutOpen = true"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.6"
            stroke-linecap="round"
            stroke-linejoin="round"
            width="16"
            height="16"
            aria-hidden="true"
          >
            <path d="M15 4h4v16h-4M10 8l-4 4 4 4M6 12h11" />
          </svg>
        </button>
      </div>
    </nav>

    <main
      id="contenido"
      class="mx-auto w-full max-w-[1240px] flex-1 px-4 pt-4 pb-24 lg:px-11 lg:pt-8 lg:pb-10"
    >
      <slot />
    </main>

    <!--
      Bottom navigation on small screens: reachable, and out of the way of content.

      The bar scrolls horizontally rather than dividing the width evenly. With eight
      destinations on a 390px screen an even split leaves 48px per label, and the labels
      run into each other. A minimum width per item keeps every one legible.
    -->
    <nav
      class="fixed inset-x-0 bottom-0 z-20 flex overflow-x-auto border-t border-border bg-surface lg:hidden"
      aria-label="Navegación principal"
    >
      <RouterLink
        v-for="item in flatNavigation"
        :key="item.name"
        :to="{ name: item.name }"
        :aria-current="isCurrent(item.name) ? 'page' : undefined"
        :class="[
          isCurrent(item.name) ? 'text-brand-700' : 'text-ink-muted',
          'flex min-h-14 min-w-20 flex-1 shrink-0 flex-col items-center justify-center gap-1',
          'px-2 text-[11px] font-medium whitespace-nowrap',
        ]"
      >
        <NavIcon
          :name="item.icon"
          :size="18"
        />
        {{ item.label }}
      </RouterLink>
    </nav>

    <!-- No native confirm(): it cannot be styled, cannot be made accessible, and blocks
         the tab. -->
    <AppModal
      :open="signOutOpen"
      title="Cerrar sesión"
      description="Se cerrará la sesión actual en este dispositivo."
      :busy="signingOut"
      @close="signOutOpen = false"
    >
      <template #actions>
        <AppButton
          variant="secondary"
          :disabled="signingOut"
          @click="signOutOpen = false"
        >
          Cancelar
        </AppButton>
        <AppButton
          :busy="signingOut"
          @click="confirmSignOut"
        >
          Cerrar sesión
        </AppButton>
      </template>
    </AppModal>
  </div>
</template>
